import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Globe } from "lucide-react";
import { useApp } from "@/store";
import { formatApiErrorDetail } from "@/lib/api";

export default function Register() {
  const { register, t } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", password: "", person_type: "individual", country: "AU",
    tax_id: "", company_name: "",
    consent_terms: false, consent_data_processing: false, consent_marketing: false,
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const taxLabel = { BR: form.person_type === "business" ? "CNPJ" : "CPF", AU: "ABN / TFN", US: "SSN / EIN" }[form.country];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_terms || !form.consent_data_processing) { toast.error("Please accept the required consents"); return; }
    setLoading(true);
    try { await register(form); toast.success(t("register_check_email")); navigate("/profile"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed"); }
    finally { setLoading(false); }
  };

  const inputCls = "w-full rounded-xl border border-gray-200 px-4 py-3 outline-none transition-colors focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20";

  return (
    <div className="mx-auto flex max-w-md flex-col px-5 py-14">
      <div className="mb-6 flex items-center gap-2 font-display text-2xl font-extrabold"><span className="grid h-9 w-9 place-items-center rounded-lg bg-[#002FA7] text-white"><Globe size={18} /></span> RentGlobe</div>
      <h1 className="font-display text-3xl font-extrabold tracking-tight">{t("nav_register")}</h1>

      <div className="mt-5 grid grid-cols-2 gap-2" data-testid="person-type-toggle">
        {[["individual", t("person_individual")], ["business", t("person_business")]].map(([v, label]) => (
          <button key={v} type="button" data-testid={`person-${v}`} onClick={() => set("person_type", v)}
            className={`rounded-xl border py-3 text-sm font-bold transition-colors ${form.person_type === v ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-gray-200 text-gray-600 hover:border-[#002FA7]"}`}>{label}</button>
        ))}
      </div>

      <form onSubmit={submit} className="mt-4 space-y-3">
        {form.person_type === "business" && (
          <input data-testid="reg-company" placeholder="Company name" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className={inputCls} />
        )}
        <input data-testid="reg-name" required placeholder={form.person_type === "business" ? "Contact name" : "Full name"} value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} />
        <input data-testid="reg-email" type="email" required placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
        <input data-testid="reg-password" type="password" required placeholder="Password" value={form.password} onChange={(e) => set("password", e.target.value)} className={inputCls} />
        <div className="grid grid-cols-2 gap-3">
          <select data-testid="reg-country" value={form.country} onChange={(e) => set("country", e.target.value)} className={inputCls}>
            <option value="BR">🇧🇷 Brazil</option>
            <option value="AU">🇦🇺 Australia</option>
            <option value="US">🇺🇸 United States</option>
          </select>
          <input data-testid="reg-taxid" placeholder={taxLabel} value={form.tax_id} onChange={(e) => set("tax_id", e.target.value)} className={inputCls} />
        </div>

        <div className="space-y-2 rounded-xl bg-gray-50 p-4 text-sm text-gray-600">
          <label className="flex items-start gap-2"><input data-testid="consent-terms" type="checkbox" checked={form.consent_terms} onChange={(e) => set("consent_terms", e.target.checked)} className="mt-1" /> <span>I accept the <Link to="/terms" target="_blank" className="font-bold text-[#002FA7] underline">Terms of Service</Link>.</span></label>
          <label className="flex items-start gap-2"><input data-testid="consent-data" type="checkbox" checked={form.consent_data_processing} onChange={(e) => set("consent_data_processing", e.target.checked)} className="mt-1" /> <span>I consent to the processing of my personal data for identity verification, per the <Link to="/privacy" target="_blank" className="font-bold text-[#002FA7] underline">Privacy Policy</Link> (LGPD / Privacy Act).</span></label>
          <label className="flex items-start gap-2"><input data-testid="consent-marketing" type="checkbox" checked={form.consent_marketing} onChange={(e) => set("consent_marketing", e.target.checked)} className="mt-1" /> Send me marketing emails (optional).</label>
        </div>

        <button data-testid="reg-submit" disabled={loading} className="w-full rounded-full bg-[#002FA7] py-3.5 font-bold text-white transition-colors hover:bg-[#002280] disabled:opacity-60">{loading ? "…" : t("nav_register")}</button>
      </form>
      <p className="mt-6 text-center text-sm text-gray-500">Already have an account? <Link to="/login" className="font-bold text-[#002FA7]">{t("nav_login")}</Link></p>
    </div>
  );
}
